import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, evaluateScope } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../App';
import { Sparkles, Loader, CheckCircle, Ban, RotateCcw, AlertTriangle, FileText } from '../components/Icons';
import { CustomSelect } from '../components/CustomSelect';
import './ScopeAnalysis.css';

const TONES = [
  { value: 'professional', label: 'Professional', desc: 'Clear & business-appropriate' },
  { value: 'firm', label: 'Firm', desc: 'Direct & assertive' },
  { value: 'friendly', label: 'Friendly', desc: 'Warm & collaborative' },
];

const IMPACT_CONFIG = {
  minor: { label: 'Minor', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.2)' },
  moderate: { label: 'Moderate', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)' },
  major: { label: 'Major', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)' },
};

const MAX_WORDS = 100;
function countWords(str) {
  const t = str.trim();
  return t === '' ? 0 : t.split(/\s+/).length;
}

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button className="sa-copy-btn" onClick={handleCopy}>
      {copied ? 'Copied' : label}
    </button>
  );
}

function ReplyBlock({ label, variant = 'default', text, onRegenerate, isRegenerating, disabled }) {
  return (
    <div className={`sa-reply-block sa-reply-${variant}`}>
      <div className="sa-reply-label">{label}</div>
      <div className="sa-reply-text">{isRegenerating ? <span className="sa-regen-placeholder">Regenerating reply…</span> : text}</div>
      <div className="sa-reply-actions">
        <CopyButton text={text || ''} />
        <button
          className="sa-regen-btn"
          onClick={onRegenerate}
          disabled={disabled}
          title="Generate a different version of this reply"
        >
          <RotateCcw size={12} className={isRegenerating ? 'spin' : ''} />
          {isRegenerating ? 'Regenerating…' : 'Regenerate'}
        </button>
      </div>
    </div>
  );
}

export default function ScopeAnalysis() {
  const { profile, planMeta } = useAuth();
  const { addToast } = useToast();

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [text, setText] = useState('');
  const [tone, setTone] = useState('professional');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [regenerating, setRegenerating] = useState(null); // 'main' | 'goodwill'
  const [error, setError] = useState(null);

  const canCustomTone = planMeta?.customTone === true;
  const isStarter = !canCustomTone;
  const monthlyUsed = profile?.monthly_eval_count || 0;
  const monthlyLimit = isStarter ? 10 : null;

  // Load projects
  useEffect(() => {
    getProjects()
      .then(data => setProjects(Array.isArray(data) ? data : []))
      .catch(() => { })
      .finally(() => setLoadingProjects(false));
  }, []);

  // Sync default tone from profile settings
  useEffect(() => {
    if (profile?.preferred_tone) setTone(profile.preferred_tone);
  }, [profile?.preferred_tone]);

  // Derived state — note: API returns camelCase (scopeKnowledge, scopeStatus)
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  // Use scopeStatus 'done' — this is always present and doesn't require inspecting the full knowledge object
  const hasScope = selectedProject?.scopeStatus === 'done';

  const wordCount = countWords(text);
  const isOverLimit = wordCount > MAX_WORDS;
  const canCheck = selectedProjectId && wordCount >= 3 && !isOverLimit && !loading && hasScope;

  // Validation hint — clean text, no emoji
  function getValidationHint() {
    if (!selectedProjectId) return 'Select a project above to continue';
    if (!hasScope) return 'Upload a scope PDF first';
    if (wordCount < 3) return 'Describe the request in at least a few words';
    if (isOverLimit) return `Over the 100-word limit by ${wordCount - MAX_WORDS} word${wordCount - MAX_WORDS !== 1 ? 's' : ''}`;
    return null;
  }
  const validationHint = getValidationHint();

  async function handleCheck() {
    if (!canCheck) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await evaluateScope({
        projectId: selectedProjectId,
        selectedText: text.trim(),
        tone: canCustomTone ? tone : 'professional',
      });
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to evaluate. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleRegenerate(replyType) {
    if (!result) return;
    setRegenerating(replyType);
    try {
      const data = await evaluateScope({
        projectId: selectedProjectId,
        selectedText: text.trim(),
        tone: canCustomTone ? tone : 'professional',
      });
      setResult(prev => ({
        ...prev,
        replies: { ...prev.replies, [replyType]: data.replies[replyType] },
      }));
    } catch {
      addToast('Regeneration failed. Please try again.', 'error');
    } finally {
      setRegenerating(null);
    }
  }

  function handleReset() {
    setResult(null);
    setError(null);
    setText('');
  }

  const isInScope = result?.status === 'in-scope';
  const impactLevel = result?.impact?.level;
  const impactCfg = IMPACT_CONFIG[impactLevel] || IMPACT_CONFIG.moderate;

  return (
    <div className="scope-analysis-page animate-fade-in">

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Scope Analysis</h1>
          <p className="page-title-sub">
            Paste a client message or describe what they&apos;re asking in your own words — both work equally. Get a verdict, the relevant SOW clause, and ready-to-send replies.
          </p>
        </div>
        {isStarter && (
          <div className="sa-usage-badge">
            <span className="sa-usage-count">{monthlyUsed}</span>
            <span className="sa-usage-sep">/</span>
            <span className="sa-usage-limit">{monthlyLimit}</span>
            <span className="sa-usage-label">checks this month</span>
          </div>
        )}
      </div>

      {/* Analysis Form */}
      <div className="sa-form card">

        {/* Row 1: Project + Tone (side by side on desktop) */}
        <div className="sa-form-top-row">

          {/* Project Selector */}
          <div className="sa-form-group">
            <label className="form-label" htmlFor="sa-project-select">
              Project <span className="sa-required">*</span>
            </label>
            {loadingProjects ? (
              <div className="skeleton" style={{ height: 42, borderRadius: 8 }} />
            ) : projects.length === 0 ? (
              <div className="sa-empty-state">
                <FileText size={18} />
                <span>No projects yet. <Link to="/app">Create one on your dashboard</Link></span>
              </div>
            ) : (
              <CustomSelect
                id="sa-project-select"
                value={selectedProjectId}
                placeholder="Select a project…"
                onChange={id => { setSelectedProjectId(id); setResult(null); setError(null); }}
                options={projects.map(p => ({
                  value: p.id,
                  label: p.projectName || 'Untitled',
                  subtitle: p.clientName || null,
                  badge: p.scopeStatus !== 'done' ? 'No PDF' : null,
                  badgeWarn: p.scopeStatus !== 'done',
                }))}
              />
            )}
            {selectedProjectId && !hasScope && (
              <div className="sa-warning-hint">
                <AlertTriangle size={13} />
                <span>
                  Upload a scope PDF first.{' '}
                  <Link to={`/app/project/${selectedProjectId}`}>Go to project</Link>
                </span>
              </div>
            )}
          </div>

          {/* Tone Selector */}
          <div className="sa-tone-group">
            <label className="form-label">
              Reply Tone
              {!canCustomTone && <span className="sa-pro-badge">Pro</span>}
            </label>
            <div className="sa-tone-pills">
              {TONES.map(t => (
                <button
                  key={t.value}
                  className={`sa-tone-pill ${tone === t.value ? 'active' : ''} ${!canCustomTone ? 'locked' : ''}`}
                  onClick={() => canCustomTone && setTone(t.value)}
                  disabled={!canCustomTone}
                  title={!canCustomTone ? 'Upgrade to Pro to customize tone' : t.desc}
                  type="button"
                >
                  {t.label}
                </button>
              ))}
            </div>
            {!canCustomTone && (
              <p className="sa-lock-hint">Custom tones are a Pro feature.</p>
            )}
          </div>
        </div>

        {/* Client Request textarea */}
        <div className="sa-form-group">
          <label className="form-label" htmlFor="sa-request-input">
            Client Request <span className="sa-required">*</span>
          </label>
          <textarea
            id="sa-request-input"
            className={`sa-textarea ${isOverLimit ? 'sa-textarea--over' : ''}`}
            placeholder={'Paste client message or describe the request in your own words:“Can you also add a mobile app version of the site?” — or — “Client is asking to redesign the login page”'}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleCheck(); }}
            rows={4}
            disabled={loading}
          />
          <div className="sa-textarea-footer">
            <span className={`sa-word-count ${isOverLimit ? 'sa-word-over' : wordCount > 80 ? 'sa-word-warn' : ''}`}>
              {wordCount} / {MAX_WORDS} words
            </span>
          </div>
        </div>

        <div className="sa-submit-row">
          {validationHint && !loading && (
            <span className="sa-hint-text">{validationHint}</span>
          )}
          <button
            className="btn btn-primary sa-check-btn"
            onClick={handleCheck}
            disabled={!canCheck}
          >
            {loading
              ? <><Loader size={15} className="spin" /> Analyzing…</>
              : <><Sparkles size={15} /> Check Scope</>
            }
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="sa-error">
          <AlertTriangle size={15} /> {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`sa-result animate-fade-in ${isInScope ? 'sa-in' : 'sa-out'}`}>

          {/* Verdict + Reasoning */}
          <div className="sa-verdict-section">
            <div className={`sa-verdict-badge ${isInScope ? 'badge-in' : 'badge-out'}`}>
              {isInScope
                ? <><CheckCircle size={15} /> In Scope</>
                : <><Ban size={15} /> Out of Scope</>
              }
            </div>
            <p className="sa-reasoning">{result.reasoning}</p>
          </div>

          {/* SOW Clause — professional reference block with copy */}
          {result.sow_clause && (
            <div className="sa-sow-clause">
              <div className="sa-sow-header">
                <span className="sa-sow-label">Agreed scope reference</span>
                <CopyButton text={result.sow_clause} label="Copy clause" />
              </div>
              <blockquote className="sa-sow-text">{result.sow_clause}</blockquote>
            </div>
          )}

          {/* Impact Assessment — qualitative only, freelancer-facing only */}
          {!isInScope && result.impact && (
            <div className="sa-impact" style={{ '--impact-color': impactCfg.color, '--impact-bg': impactCfg.bg, '--impact-border': impactCfg.border }}>
              <div className="sa-impact-header">
                <span className="sa-impact-title">Impact to you</span>
                <span className="sa-impact-note">For your reference — not in your replies</span>
              </div>
              <div className="sa-impact-level">
                <span className="sa-impact-badge">
                  <span className="sa-impact-dot" />
                  {impactCfg.label} Impact
                </span>
              </div>
              {result.impact.summary && (
                <p className="sa-impact-summary">{result.impact.summary}</p>
              )}
            </div>
          )}

          {/* Reply Messages */}
          <div className="sa-replies">
            <div className="sa-replies-header">Suggested Replies</div>
            <ReplyBlock
              label={isInScope ? 'Confirmation Reply' : 'Decline Reply'}
              variant={isInScope ? 'positive' : 'negative'}
              text={result.replies?.main}
              onRegenerate={() => handleRegenerate('main')}
              isRegenerating={regenerating === 'main'}
              disabled={!!regenerating || loading}
            />
            <ReplyBlock
              label="Goodwill Offer"
              variant="neutral"
              text={result.replies?.goodwill}
              onRegenerate={() => handleRegenerate('goodwill')}
              isRegenerating={regenerating === 'goodwill'}
              disabled={!!regenerating || loading}
            />
          </div>

          <button className="sa-reset-btn" onClick={handleReset}>
            ← Check another request
          </button>
        </div>
      )}
    </div>
  );
}
